import { ActivityIndicator, ScrollView, StyleSheet } from 'react-native'
import { Theme, ThemedText, ThemedView } from '@app/theme'
import ScreenWrapper from '@app/hoc/ScreenWrapper'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import { PrivateStackParamList } from '@app/navigationApp/types'
import SystemError from 'assets/Icons/SystemErrorIcon'
import Button from '@app/sharedComponents/Button/Button'
import { useTheme } from '@shopify/restyle'
import Stepper from '@app/sharedComponents/Stepper/Stepper'
import CommonHeader from '@app/sharedComponents/Header/Header'
import { useEffect, useState, useRef } from 'react'
import { getStepper } from '../VerifyBankAccount/services/verifyBankService'
import { useAppSelector, useAppDispatch } from '@app/store/hooks'
import { getDspAgreement, checkDspAgreementStatus } from './dspAgreementService'
import { showErrorSheet } from '@app/store/slices/errorSlice'
import { WebView, WebViewMessageEvent } from 'react-native-webview'
import DspAgreementSuccessModal from './Components/DspAgreementSuccessModal'
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet'
import DspAgreementErrorModal from './Components/DspAgreementErrorModal'
import { updateLinkedApplication } from '@app/store/slices/authSlice'
import { getNextRoute } from '@app/navigationApp/navigationUtil'
import { updatePartnerParams } from '@app/store/slices/platformSlice'
import { trackEvent } from '@app/utils'
import { useStepperData } from '@app/hooks/useStepperData'
import { APPLICATION_STATE } from '@app/constants/platformPartners'
import { getNextStepId } from '../KYCStepper/services/service'

type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'DSP_KFS'>


const DSPAgreement = ({ route, navigation }: ScreenProps) => {

    const { colors } = useTheme<Theme>()
    const dispatch = useAppDispatch();

    const [responseAgreementStatus, setResponseAgreementStatus] = useState<'success' | 'failure' | 'waiting'>('waiting');

    const { stepperData, isLoading: stepperLoading } = useStepperData()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const userDetails = useAppSelector(state => state.auth.userDetails);
    const [dspAgreementData, setDspAgreementData] = useState<any>(null);




    const fetchDspAgreement = async () => {
        try {
            const response = await getDspAgreement({ applicationId: userDetails?.linkedApplications[0]?.applicationId });
            setDspAgreementData(response);
        } catch (error: any) {
            if (error?.data?.voltErrorCode === "PENDING_APPROVAL") {
                const updatedApplicationObj = await getNextStepId({applicationId : userDetails?.linkedApplications[0]?.applicationId })
                // update linked application 
                dispatch(updateLinkedApplication(updatedApplicationObj))
                if (updatedApplicationObj?.applicationState === APPLICATION_STATE.BLOCKED || updatedApplicationObj?.applicationState === APPLICATION_STATE.DENIED) {
                    navigation.reset({
                        index: 0,
                        routes: [{ name: 'BLOCKED' }]
                    })
                } else {
                     navigation.navigate(getNextRoute(updatedApplicationObj?.currentStepId, updatedApplicationObj?.applicationState, updatedApplicationObj?.stepStatusMap) as any)
                }
            } else {
                dispatch(showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: error?.data?.message,
                    closeOnBackdropPress: true,
                    errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                    ctaLabel: 'Okay'
                }));
            }
        }
    }

    const onMessage = async (event: WebViewMessageEvent) => {
        if (event?.nativeEvent?.data) {
            let eventData;

            try {
                eventData = typeof event.nativeEvent.data === "string"
                    ? JSON.parse(event.nativeEvent.data)
                    : event.nativeEvent.data;
            } catch (error) {
                console.error("Failed to parse event data:", error);
                return;
            }
            if (eventData?.success === true || eventData?.success === "true") {
                trackEvent('DSP_AGREEMENT_COMPLETED')
                handleAgreementCompleted();
            } else if (eventData?.success === false || eventData?.success === "false") {
                trackEvent('DSP_AGREEMENT_FAILURE')
                handleAgreementFailure();
            }
        }
    };

    const checkAgreementStatus = async () => {
        try {
            const response = await checkDspAgreementStatus({ applicationId: userDetails?.linkedApplications[0]?.applicationId });
            if (response?.status === 'SUCCESS') {
                dispatch(updateLinkedApplication(response?.updatedApplicationObj));
            }
            if (response?.stepResponseObject?.status?.toLowerCase() === 'completed' || response?.stepResponseObject?.status?.toLowerCase() === 'accepted') {
                setResponseAgreementStatus('success')
            }
            else {
                setResponseAgreementStatus('failure')
            }
        } catch (error: any) {
            dispatch(showErrorSheet({
                isErrorSheetVisible: true,
                errorMessage: error?.data?.message,
                closeOnBackdropPress: true,
                errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                ctaLabel: 'Okay'
            }));
        }
    }

    const handleAgreementCompleted = () => {
        // setResponseAgreementStatus('success')
        checkAgreementStatus();
    }

    const handleAgreementFailure = () => {
        setResponseAgreementStatus('failure')
    }

    const onSuccess = () => {
        navigation.reset({
            index: 0,
            routes: [{ name: 'KYC_STEPPER' }, { name: getNextRoute(userDetails?.linkedApplications[0]?.currentStepId, userDetails?.linkedApplications[0]?.applicationState, userDetails?.linkedApplications[0]?.stepStatusMap) }]
        })
    }



    useEffect(() => {
        setIsLoading(true)
        fetchDspAgreement().finally(() => {
            setIsLoading(false)
        })
        dispatch(updatePartnerParams({ showCall: true }))
    }, [])



    return (
        <ThemedView flex={1} bg='bg-surface-primary'>
            <ScreenWrapper screenName={route?.name} additionalProperties={{ newArchRoute: true }}>
                <CommonHeader label='Review Agreement' navigation={navigation} hideBottomBorder pageName={route.name} />
                <Stepper isLoading={stepperLoading} stepperData={stepperData} />
                 { isLoading ? (
                    <ThemedView alignItems='center' justifyContent='center' flex={1}> 
                        <ActivityIndicator size={12} color={colors['text-action-primary']} />
                    </ThemedView>
                ) : (
                    <WebView
                        scalesPageToFit={true}
                        originWhitelist={['*']}
                        javaScriptEnabled={true}
                        javaScriptCanOpenWindowsAutomatically
                        allowFileAccess
                        domStorageEnabled={true}
                        mixedContentMode={'always'}
                        mediaPlaybackRequiresUserAction={false}
                        allowsInlineMediaPlayback={true}
                        onLoadEnd={() => {
                        }}
                        onMessage={onMessage}
                        source={{ uri: dspAgreementData?.stepResponseObject?.link }} style={{ flex: 1, height: '100%' }} />
                )}

            </ScreenWrapper>
            <BottomSheetContainer
                // closeOnBackdropPress={false}
                isVisible={responseAgreementStatus !== 'waiting'}
                close={() => {
                    setResponseAgreementStatus('waiting')
                }}
                onClose={() => {
                    setResponseAgreementStatus('waiting')
                }}
                dragComponent={<ThemedView style={{ width: 40, height: 4, backgroundColor: colors['border-primary'], borderRadius: 10 }} />}
                containerStyle={{ borderTopRightRadius: 16, borderTopLeftRadius: 16 }}
                borderTopRightRadius={10}
                borderTopLeftRadius={10}
                closeOnBackdropPress={responseAgreementStatus==='failure'}
            >
                {responseAgreementStatus === 'success' && <DspAgreementSuccessModal onPress={onSuccess} />}
                {responseAgreementStatus === 'failure' && <DspAgreementErrorModal onContactUsPress={() => {
                    navigation.navigate('CONTACT_US')
                }} />}
            </BottomSheetContainer>
        </ThemedView>
    )
}

export default DSPAgreement

const styles = StyleSheet.create({})